import { useEffect, useRef } from "react";
import { styled } from "@mui/material";
import Modal from "components/Modal";
import Typography from "components/Typography";
import Div from "components/primitives/Div";
import CheckIcon from "components/Icons/CheckIcon";

/** How long the card sits on screen before closing itself. Overridable via `autoDismissMs`. */
const DEFAULT_AUTO_DISMISS_MS = 3000;

/**
 * `Modal`'s container fills the viewport (`calc(100% - x)`, capped at the `md`
 * breakpoint) and centres *itself*, not its children — so without this the card
 * sits in that box's top-left corner rather than mid-screen. Module scope keeps
 * the reference stable across renders.
 */
const CENTRED_CONTAINER_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
} as const;

const Card = styled(Div)(({ theme }) => ({
  pointerEvents: "auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  width: "240px",
  padding: theme.spacing(4, 3),
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.secondary1[10],
  textAlign: "center",
}));

const CheckCircle = styled(Div)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  backgroundColor: theme.palette.success.main,

  ".MuiSvgIcon-root": {
    color: theme.palette.common.white,
    fontSize: "20px",
  },
}));

const Message = styled(Typography)({
  fontWeight: 500,
});

interface SuccessModalProps {
  open: boolean;
  onClose(): void;
  /** Already-translated confirmation copy. */
  message: string;
  autoDismissMs?: number;
}

/**
 * Transient confirmation card shown after a successful submission — a green check over a short
 * message, on the mint success background used across the app.
 *
 * Auto-dismisses to match the designs, but stays closable via Escape and the backdrop: the dismiss
 * window is short enough that a screen reader may not finish announcing it, so the message is also
 * given a polite live region rather than relying on the timer alone.
 */
function SuccessModal(props: SuccessModalProps) {
  const { open, onClose, message, autoDismissMs = DEFAULT_AUTO_DISMISS_MS } = props;

  /**
   * Held in a ref so `onClose` is not an effect dependency. A caller passing an
   * inline arrow would otherwise hand us a new function every render, tearing
   * down and restarting the timer each time — on a parent that re-renders more
   * often than the dismiss delay, the modal would never close itself.
   */
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    const timeout = window.setTimeout(() => onCloseRef.current(), autoDismissMs);

    return () => window.clearTimeout(timeout);
  }, [open, autoDismissMs]);

  return (
    <Modal open={open} toggleOpen={onClose} sx={CENTRED_CONTAINER_SX}>
      <Card role="status" aria-live="polite">
        <CheckCircle>
          <CheckIcon />
        </CheckCircle>
        <Message variant="TextM">{message}</Message>
      </Card>
    </Modal>
  );
}

export default SuccessModal;
